import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import api from '../../api/client';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Award, Download, Ban, Loader2, BadgeCheck, ShieldAlert } from 'lucide-react';
import { TraineeFilterBar, useTraineeFilter, ALL } from './TraineeFilterContext';

const pct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(0)}%`);

const READY_STATE = {
  floor_ready: { label: 'Floor ready', cls: 'bg-emerald-100 text-emerald-700' },
  on_track:    { label: 'On track',    cls: 'bg-warm-200 text-warm-800' },
  at_risk:     { label: 'At risk',     cls: 'bg-amber-100 text-amber-700' },
  not_ready:   { label: 'Not ready',   cls: 'bg-rose-100 text-rose-700' },
  not_scored:  { label: 'Not scored',  cls: 'bg-slate-100 text-slate-600' },
};

export default function CertificationsTab() {
  const { get, post } = useApi();
  const { batchId, scopeQS } = useTraineeFilter();
  const [program, setProgram] = useState('');
  const [eligible, setEligible] = useState([]);
  const [certs, setCerts] = useState([]);
  const [gating, setGating] = useState('off');
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const scope = scopeQS();
      const bq = scope ? `?${scope}` : '';
      const [e, c] = await Promise.all([
        get(`/trainees/certifications/eligibility${bq}`),
        get(`/trainees/certifications${bq}`),
      ]);
      setEligible(e.trainees || []);
      setGating(e.readiness_gating || 'off');
      setCerts(c.certifications || []);
      setSelected(new Set());
    } catch {
      toast.error('Failed to load certifications');
    } finally { setLoading(false); }
  }, [get, scopeQS]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const issue = async () => {
    if (!program.trim()) { toast.error('Enter a program name'); return; }
    if (selected.size === 0) { toast.error('Select at least one trainee'); return; }
    setIssuing(true);
    try {
      const res = await post('/trainees/certifications/bulk', {
        trainee_ids: Array.from(selected),
        program_name: program.trim(),
        batch_id: batchId === ALL ? null : batchId,
      });
      const blocked = res.blocked_by_readiness || 0;
      toast.success(
        `Issued ${res.issued} certificate(s)` +
        (blocked ? `, ${blocked} blocked by readiness gate` : res.skipped ? `, ${res.skipped} skipped` : ''));
      setProgram('');
      await load();
    } catch { toast.error('Issue failed'); } finally { setIssuing(false); }
  };

  const download = async (cert) => {
    try {
      const res = await api.get(`/trainees/certifications/${cert.certification_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `certificate-${cert.verify_code}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const revoke = async (cert) => {
    if (!window.confirm(`Revoke certificate for ${cert.trainee_name}?`)) return;
    try {
      await post(`/trainees/certifications/${cert.certification_id}/revoke`, { reason: 'Revoked by trainer' });
      toast.success('Certificate revoked');
      await load();
    } catch { toast.error('Revoke failed'); }
  };

  return (
    <div className="space-y-6" data-testid="certifications-tab">
      <div className="flex flex-wrap items-end gap-3">
        <TraineeFilterBar />
        <div className="space-y-1.5 flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-muted-foreground">Program / Course name</label>
          <Input value={program} onChange={(e) => setProgram(e.target.value)}
                 placeholder="e.g. New Hire Training — Voice Process" data-testid="cert-program-input" />
        </div>
        <Button onClick={issue} disabled={issuing} data-testid="cert-issue-btn">
          {issuing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Award className="w-4 h-4 mr-2" />}
          Issue for selected ({selected.size})
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/30 font-display font-semibold text-sm flex items-center gap-2">
          Eligibility
          {gating === 'advisory' && (
            <Badge variant="outline" className="text-[10px] font-normal" data-testid="gating-banner-advisory">
              Readiness advisory — shown for context, does not block
            </Badge>
          )}
          {gating === 'required' && (
            <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] font-normal" data-testid="gating-banner-required">
              <ShieldAlert className="w-3 h-3 mr-1" />Readiness gate active — only floor-ready trainees can be certified
            </Badge>
          )}
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Trainee</TableHead>
                <TableHead>Attendance</TableHead>
                <TableHead>Assessment</TableHead>
                <TableHead>Training</TableHead>
                {gating !== 'off' && <TableHead>Readiness</TableHead>}
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eligible.length === 0 && <TableRow><TableCell colSpan={gating !== 'off' ? 7 : 6} className="text-center text-muted-foreground py-6">No trainees in scope.</TableCell></TableRow>}
              {eligible.map(t => {
                const blocked = gating === 'required' && !t.already_certified
                  && t.readiness_state !== 'floor_ready';
                const rs = READY_STATE[t.readiness_state] || null;
                return (
                <TableRow key={t.trainee_id} data-testid={`cert-elig-row-${t.trainee_id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(t.trainee_id)}
                      disabled={t.already_certified || blocked}
                      onCheckedChange={() => toggle(t.trainee_id)}
                      data-testid={`cert-select-${t.trainee_id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{t.name}{t.employee_id ? <span className="text-muted-foreground text-xs ml-2">{t.employee_id}</span> : null}</TableCell>
                  <TableCell>{pct(t.attendance_pct)}</TableCell>
                  <TableCell>{pct(t.assessment_pct)}</TableCell>
                  <TableCell className="text-sm">{t.training_status || '—'}</TableCell>
                  {gating !== 'off' && (
                    <TableCell data-testid={`cert-readiness-${t.trainee_id}`}>
                      {rs
                        ? <Badge className={`${rs.cls} border-0`}>{rs.label}{t.readiness_score != null ? ` · ${t.readiness_score}` : ''}</Badge>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                  <TableCell>
                    {t.already_certified
                      ? <Badge className="bg-emerald-100 text-emerald-700"><BadgeCheck className="w-3 h-3 mr-1" />Certified</Badge>
                      : blocked
                        ? <Badge className="bg-rose-100 text-rose-700" title={(t.ineligible_reasons || []).join('; ')} data-testid={`cert-blocked-${t.trainee_id}`}>
                            <Ban className="w-3 h-3 mr-1" />Blocked
                          </Badge>
                        : t.eligible
                          ? <Badge className="bg-amber-100 text-amber-700">Eligible</Badge>
                          : <Badge variant="secondary" title={(t.ineligible_reasons || []).join('; ')}>Not yet</Badge>}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/30 font-display font-semibold text-sm">Issued certificates</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Trainee</TableHead>
              <TableHead>Program</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {certs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No certificates issued yet.</TableCell></TableRow>}
            {certs.map(c => (
              <TableRow key={c.certification_id} data-testid={`cert-row-${c.certification_id}`}>
                <TableCell className="font-medium">{c.trainee_name}</TableCell>
                <TableCell>{c.program_name}</TableCell>
                <TableCell className="text-sm">{(c.issued_at || '').slice(0, 10)}</TableCell>
                <TableCell>
                  {c.status === 'active'
                    ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                    : <Badge variant="secondary" className="bg-rose-100 text-rose-700">Revoked</Badge>}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => download(c)} data-testid={`cert-download-${c.certification_id}`}>
                    <Download className="w-3.5 h-3.5 mr-1" />PDF
                  </Button>
                  {c.status === 'active' && (
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => revoke(c)} data-testid={`cert-revoke-${c.certification_id}`}>
                      <Ban className="w-3.5 h-3.5 mr-1" />Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
